/**
 * CareerTransitionHome — /career-transition
 *
 * Landing page for Career Transition (Product #2) — the quarterback product.
 *
 * Integrates Designer Session 1 (Career Transition Landing v2) as a React +
 * MUI page. Colours come entirely from the MUI theme palette so the page
 * respects light/dark mode like every other page in the app.
 *
 * Auth-aware roadmap section: if the user is signed in AND has a roadmap,
 * show theirs; otherwise show the anonymized example.
 */

import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import {
  AUDIENCES,
  FRAMEWORKS,
  ROADMAP_TIER_DISPLAY,
  TierGate,
  TransitionAudience,
  getAudienceByKey,
} from "../data/careerTransition";
import { getLatestRoadmap, RoadmapResponse } from "../api/client";
import { L } from "../utils/localized";
import { useAuth } from "../hooks/useAuth";
import { RoadmapMock } from "../components/career-transition/RoadmapMock";
import { ctHeroCta } from "../utils/heroCtas";

// Inline monospace stack — this is a font, not a colour token.
const MONO = `ui-monospace, 'SF Mono', Menlo, Consolas, monospace`;

export default function CareerTransitionHome() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation("pages-core");
  const [audienceKey, setAudienceKey] =
    useState<TransitionAudience>("swe_to_ai");
  const audience = getAudienceByKey(audienceKey, i18n.language);
  const audiences = L(AUDIENCES, i18n.language);
  const frameworks = L(FRAMEWORKS, i18n.language);
  const roadmapTiers = L(ROADMAP_TIER_DISPLAY, i18n.language);

  // Tier-pill colour mapping on framework cards. Fully theme-derived so
  // pills read correctly in both light and dark mode.
  const FRAMEWORK_TIER_STYLE: Record<
    TierGate,
    { bg: string; color: string; label: string }
  > = {
    free: {
      bg: alpha(theme.palette.success.main, 0.12),
      color: theme.palette.success.dark,
      label: t("CareerTransitionHome.tierFreeLabel"),
    },
    pro: {
      bg: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.dark,
      label: t("CareerTransitionHome.tierProLabel"),
    },
    all_access: {
      bg: alpha(theme.palette.primary.main, 0.12),
      color: theme.palette.primary.main,
      label: t("CareerTransitionHome.tierAllAccessLabel"),
    },
  };

  // Q3b: attempt to load the authenticated user's latest roadmap. If they
  // have none (404) or aren't logged in, the RoadmapMock falls back to an
  // anonymized example.
  const { data: roadmap } = useQuery<RoadmapResponse>({
    queryKey: ["career-transition-landing-roadmap"],
    queryFn: getLatestRoadmap,
    enabled: isAuthenticated,
    retry: false,
  });

  const goToPricing = () => navigate("/pricing");

  const heroCta = ctHeroCta(roadmap, t);

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        color: "text.primary",
      }}
    >
      {/* ----- Hero ----- */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          py: { xs: 8, md: 10 },
          px: { xs: 3, md: 5 },
        }}
      >
        <Container maxWidth="lg" sx={{ textAlign: "center" }}>
          <Chip
            label={t("CareerTransitionHome.heroChipLabel")}
            size="small"
            sx={{
              mb: 3.5,
              fontWeight: 500,
              fontSize: "0.7rem",
              letterSpacing: "0.08em",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
              borderRadius: 1,
            }}
          />
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              lineHeight: 1.05,
              fontWeight: 500,
              letterSpacing: "-0.03em",
              color: "text.primary",
              maxWidth: 920,
              mx: "auto",
              textWrap: "balance",
            }}
          >
            <Trans
              i18nKey="CareerTransitionHome.heroTitle"
              t={t}
              components={[
                <Box component="span" key="span0" sx={{ color: "primary.main" }} />,
              ]}
            />
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "1rem", md: "1.125rem" },
              lineHeight: 1.55,
              color: "text.secondary",
              maxWidth: 720,
              mx: "auto",
              mt: 3,
              mb: 4.5,
            }}
          >
            {t("CareerTransitionHome.heroBody")}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="center"
            mb={3.5}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<AutoAwesomeIcon />}
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(heroCta.href)}
              sx={{
                fontWeight: 500,
                px: 3,
                py: 1.4,
                fontSize: "0.95rem",
                textTransform: "none",
                boxShadow: theme.shadows[1],
                "&:hover": { boxShadow: theme.shadows[3] },
              }}
            >
              {heroCta.label}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={goToPricing}
              sx={{
                borderWidth: 1.5,
                fontWeight: 500,
                px: 3,
                py: 1.4,
                fontSize: "0.95rem",
                textTransform: "none",
                "&:hover": {
                  borderWidth: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              {t("CareerTransitionHome.seeAllAccessPricingButton")}
            </Button>
          </Stack>
          <Stack
            direction="row"
            spacing={3}
            justifyContent="center"
            flexWrap="wrap"
            sx={{ rowGap: 1, fontSize: "0.875rem", color: "text.secondary" }}
          >
            <TrustChip label={t("CareerTransitionHome.trustChipRoadmapFree")} />
            <TrustChip label={t("CareerTransitionHome.trustChipRoutesInto")} />
            <TrustChip label={t("CareerTransitionHome.trustChipClaudePowered")} />
          </Stack>
        </Container>
      </Box>

      {/* ----- Trust bar (credential + proof) ----- */}
      {/* TODO(legal): credential copy pending legal review — approved by
          David for this Session 1 probe per handoff Q2. Kept as prose so
          edits don't require component changes. */}
      <Box
        sx={{
          py: { xs: 5, md: 6 },
          px: { xs: 3, md: 5 },
          bgcolor: "background.paper",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1px 1fr" },
              gap: { xs: 4, md: 6 },
              alignItems: "center",
            }}
          >
            <TrustColumn
              label={t("CareerTransitionHome.credentialLabel")}
              body={
                <Trans
                  i18nKey="CareerTransitionHome.credentialBody"
                  t={t}
                  components={[<b key="b0" style={{ fontWeight: 500 }} />]}
                />
              }
            />
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                width: "1px",
                height: 56,
                bgcolor: "divider",
                justifySelf: "center",
              }}
            />
            <TrustColumn
              label={t("CareerTransitionHome.proofLabel")}
              body={
                <Trans
                  i18nKey="CareerTransitionHome.proofBody"
                  t={t}
                  components={[
                    <b key="b0" style={{ fontWeight: 500 }} />,
                    <b key="b1" style={{ fontWeight: 500 }} />,
                  ]}
                />
              }
            />
          </Box>
        </Container>
      </Box>

      {/* ----- Roadmap preview ----- */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: { xs: 3, md: 5 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <SectionEyebrow>{t("CareerTransitionHome.whatArrivesEyebrow")}</SectionEyebrow>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.25rem" },
                fontWeight: 500,
                letterSpacing: "-0.025em",
                color: "text.primary",
                lineHeight: 1.15,
                mt: 1.2,
              }}
            >
              {t("CareerTransitionHome.whatArrivesTitle")}
            </Typography>
          </Box>
          <Box sx={{ maxWidth: 960, mx: "auto" }}>
            <RoadmapMock roadmap={roadmap} />
          </Box>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: "text.disabled",
              textAlign: "center",
              mt: 2,
              fontFamily: MONO,
            }}
          >
            {roadmap
              ? t("CareerTransitionHome.roadmapPreviewCaptionOwn")
              : t("CareerTransitionHome.roadmapPreviewCaptionExample")}
          </Typography>
        </Container>
      </Box>

      {/* ----- Audience toggle ----- */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 3, md: 5 } }}>
        <Container maxWidth="lg">
          <Box mb={3.5}>
            <SectionEyebrow>{t("CareerTransitionHome.pickYourTransitionEyebrow")}</SectionEyebrow>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: "1.875rem", md: "2.125rem" },
                fontWeight: 500,
                letterSpacing: "-0.022em",
                color: "text.primary",
                lineHeight: 1.15,
                mt: 1.2,
              }}
            >
              {t("CareerTransitionHome.threeTransitionsTitle")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              mb: 3.5,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              borderRadius: 1.25,
              p: 0.5,
              bgcolor: "background.paper",
            }}
          >
            {audiences.map((a) => {
              const active = a.key === audienceKey;
              return (
                <Button
                  key={a.key}
                  onClick={() => setAudienceKey(a.key)}
                  disableRipple
                  sx={{
                    px: 2.25,
                    py: 1.25,
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    letterSpacing: "0.01em",
                    textTransform: "none",
                    borderRadius: 0.75,
                    bgcolor: active
                      ? alpha(theme.palette.primary.main, 0.12)
                      : "transparent",
                    color: active ? "primary.main" : "text.secondary",
                    "&:hover": {
                      bgcolor: active
                        ? alpha(theme.palette.primary.main, 0.12)
                        : alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  {a.shortLabel}
                </Button>
              );
            })}
          </Box>
          {audience && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4.5 },
                borderRadius: 1.5,
                bgcolor: "background.paper",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                borderLeft: `6px solid ${theme.palette.primary.main}`,
                boxShadow: theme.shadows[1],
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 300px" },
                gap: { xs: 3, md: 5 },
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    color: "primary.main",
                    letterSpacing: "0.08em",
                    mb: 1.5,
                    textTransform: "uppercase",
                  }}
                >
                  {audience.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    lineHeight: 1.65,
                    color: "text.primary",
                    mb: 3,
                  }}
                >
                  {audience.positioning}
                </Typography>
                <Stack spacing={2}>
                  <InsightRow
                    label={t("CareerTransitionHome.biggestGapLabel")}
                    labelColor={theme.palette.error.main}
                    body={audience.biggestGap}
                  />
                  <InsightRow
                    label={t("CareerTransitionHome.biggestAssetLabel")}
                    labelColor={theme.palette.success.dark}
                    body={audience.biggestAsset}
                  />
                </Stack>
              </Box>
              <Stack spacing={1.5}>
                <StatCard
                  label={t("CareerTransitionHome.marketDemandLabel")}
                  value={
                    audience.marketDemand === "very_high"
                      ? t("CareerTransitionHome.marketDemandVeryHigh")
                      : audience.marketDemand === "high"
                      ? t("CareerTransitionHome.marketDemandHigh")
                      : t("CareerTransitionHome.marketDemandModerate")
                  }
                />
                <StatCard
                  label={t("CareerTransitionHome.typicalTimelineLabel")}
                  value={audience.typicalTimeline}
                />
                <StatCard
                  label={t("CareerTransitionHome.compDeltaLabel")}
                  value={audience.compensationDelta}
                  small
                />
              </Stack>
            </Paper>
          )}
        </Container>
      </Box>

      {/* ----- Frameworks ----- */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 3, md: 5 },
          bgcolor: "background.paper",
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 720, mb: 5 }}>
            <SectionEyebrow>
              {t("CareerTransitionHome.sixFrameworksEyebrow")}
            </SectionEyebrow>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: "1.875rem", md: "2.125rem" },
                fontWeight: 500,
                letterSpacing: "-0.022em",
                color: "text.primary",
                lineHeight: 1.15,
                mt: 1.2,
                mb: 1.75,
              }}
            >
              {t("CareerTransitionHome.contentNoOtherProductTitle")}
            </Typography>
            <Typography
              sx={{
                fontSize: "1rem",
                color: "text.secondary",
                lineHeight: 1.65,
              }}
            >
              {t("CareerTransitionHome.frameworksIntro")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
                lg: "repeat(3, 1fr)",
              },
              gap: 2.5,
            }}
          >
            {frameworks.map((f) => {
              const tierStyle = FRAMEWORK_TIER_STYLE[f.tier];
              return (
                <Paper
                  key={f.slug}
                  elevation={0}
                  onClick={() =>
                    navigate(`/career-transition/framework/${f.slug}`)
                  }
                  sx={{
                    p: 3,
                    borderRadius: 1.5,
                    bgcolor: "background.paper",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    boxShadow: theme.shadows[1],
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    minHeight: 280,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: "primary.main",
                      transform: "translateY(-2px)",
                      boxShadow: theme.shadows[3],
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: MONO,
                        fontSize: "0.8rem",
                        fontWeight: 500,
                      }}
                    >
                      {String(f.number).padStart(2, "0")}
                    </Box>
                    <Box
                      sx={{
                        fontSize: "0.625rem",
                        fontWeight: 500,
                        letterSpacing: "0.06em",
                        px: 1,
                        py: 0.5,
                        borderRadius: 0.75,
                        bgcolor: tierStyle.bg,
                        color: tierStyle.color,
                      }}
                    >
                      {tierStyle.label}
                    </Box>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      fontSize: "0.7rem",
                      color: "text.disabled",
                      fontWeight: 500,
                    }}
                  >
                    <span>#{String(f.number).padStart(2, "0")}</span>
                    <span>·</span>
                    <span>{f.studyTime}</span>
                  </Stack>
                  <Typography
                    sx={{
                      fontSize: "1.125rem",
                      fontWeight: 500,
                      color: "text.primary",
                      letterSpacing: "-0.018em",
                      lineHeight: 1.3,
                    }}
                  >
                    {f.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      color: "text.secondary",
                      lineHeight: 1.55,
                      flex: 1,
                    }}
                  >
                    {f.tagline}
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.625rem",
                        fontWeight: 500,
                        color: "primary.main",
                        letterSpacing: "0.05em",
                        mb: 0.5,
                      }}
                    >
                      {t("CareerTransitionHome.keyInsightLabel")}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "text.secondary",
                        lineHeight: 1.55,
                      }}
                    >
                      {f.keyInsight}
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* ----- Roadmap pricing ----- */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 3, md: 5 } }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3.5, md: 6 },
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              boxShadow: theme.shadows[1],
            }}
          >
            <Stack spacing={1.5} mb={4}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AutoAwesomeIcon sx={{ color: "primary.main", fontSize: 18 }} />
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    color: "primary.main",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {t("CareerTransitionHome.aiRoadmapGeneratorEyebrow")}
                </Typography>
              </Stack>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: "1.875rem", md: "2.125rem" },
                  fontWeight: 500,
                  letterSpacing: "-0.022em",
                  color: "text.primary",
                  lineHeight: 1.15,
                }}
              >
                {t("CareerTransitionHome.firstRoadmapFreeTitle")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "1rem",
                  color: "text.secondary",
                  lineHeight: 1.6,
                  maxWidth: 720,
                }}
              >
                {t("CareerTransitionHome.roadmapGeneratorIntro")}
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              {roadmapTiers.map((t) => (
                <Paper
                  key={t.tier}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 1.25,
                    bgcolor: "background.paper",
                    border: `1px solid ${t.color}40`,
                    borderTop: `4px solid ${t.color}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 800,
                      color: t.color,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      mb: 0.75,
                    }}
                  >
                    {t.label}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      fontSize: "1.125rem",
                      fontWeight: 500,
                      color: "text.primary",
                      letterSpacing: "-0.018em",
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: t.color,
                      }}
                    />
                    <span>{t.generations}</span>
                  </Stack>
                  <Typography
                    sx={{
                      fontSize: "0.8125rem",
                      color: "text.secondary",
                      lineHeight: 1.55,
                    }}
                  >
                    {t.sublabel}
                  </Typography>
                </Paper>
              ))}
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              mt={4}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<AutoAwesomeIcon />}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate(heroCta.href)}
                sx={{
                  fontWeight: 500,
                  textTransform: "none",
                  px: 3,
                  py: 1.4,
                  boxShadow: theme.shadows[1],
                  "&:hover": { boxShadow: theme.shadows[3] },
                }}
              >
                {roadmap
                  ? t("CareerTransitionHome.viewYourRoadmapButton")
                  : t("CareerTransitionHome.generateRoadmapFreeButton")}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={goToPricing}
                sx={{
                  borderWidth: 1.5,
                  fontWeight: 500,
                  textTransform: "none",
                  px: 3,
                  py: 1.4,
                  "&:hover": {
                    borderWidth: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                {t("CareerTransitionHome.seeAllAccessPricingButton")}
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>

      {/* ----- Proof (case studies) ----- */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 3, md: 5 },
          bgcolor: "background.paper",
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Box mb={5}>
            <SectionEyebrow>{t("CareerTransitionHome.outcomesEyebrow")}</SectionEyebrow>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: "1.875rem", md: "2.125rem" },
                fontWeight: 500,
                letterSpacing: "-0.022em",
                color: "text.primary",
                lineHeight: 1.15,
                mt: 1.2,
              }}
            >
              {t("CareerTransitionHome.twoRecentTransitionsTitle")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
            }}
          >
            <CaseStudyCard
              from={t("CareerTransitionHome.caseStudyAFrom")}
              to={t("CareerTransitionHome.caseStudyATo")}
              time={t("CareerTransitionHome.caseStudyATime")}
              quote={t("CareerTransitionHome.caseStudyAQuote")}
              id={t("CareerTransitionHome.caseStudyAId")}
            />
            <CaseStudyCard
              from={t("CareerTransitionHome.caseStudyBFrom")}
              to={t("CareerTransitionHome.caseStudyBTo")}
              time={t("CareerTransitionHome.caseStudyBTime")}
              quote={t("CareerTransitionHome.caseStudyBQuote")}
              id={t("CareerTransitionHome.caseStudyBId")}
            />
          </Box>
        </Container>
      </Box>

      {/* ----- Final CTA ----- */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          px: { xs: 3, md: 5 },
          bgcolor: "background.default",
          textAlign: "center",
        }}
      >
        <Container maxWidth="sm">
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.75rem" },
              fontWeight: 500,
              letterSpacing: "-0.028em",
              lineHeight: 1.1,
              mb: 2,
              color: "text.primary",
              textWrap: "balance",
            }}
          >
            {t("CareerTransitionHome.finalCtaTitle")}
          </Typography>
          <Typography
            sx={{
              fontSize: "1.0625rem",
              color: "text.secondary",
              mb: 4,
              lineHeight: 1.55,
            }}
          >
            {t("CareerTransitionHome.finalCtaBody")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AutoAwesomeIcon />}
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate(heroCta.href)}
            sx={{
              fontWeight: 500,
              textTransform: "none",
              px: 3.5,
              py: 1.6,
              fontSize: "0.95rem",
              boxShadow: theme.shadows[1],
              "&:hover": { boxShadow: theme.shadows[3] },
            }}
          >
            {roadmap
              ? t("CareerTransitionHome.viewYourRoadmapButton")
              : t("CareerTransitionHome.startReadinessAssessmentButton")}
          </Button>
        </Container>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Local sub-components
// ---------------------------------------------------------------------------

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: "0.7rem",
        fontWeight: 700,
        color: "primary.main",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </Typography>
  );
}

function TrustChip({ label }: { label: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
      <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
        {label}
      </Typography>
    </Stack>
  );
}

function TrustColumn({
  label,
  body,
}: {
  label: string;
  body: React.ReactNode;
}) {
  return (
    <Box sx={{ fontSize: "0.875rem", color: "text.secondary", lineHeight: 1.6 }}>
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 500,
          color: "text.disabled",
          letterSpacing: "0.08em",
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Box>{body}</Box>
    </Box>
  );
}

function InsightRow({
  label,
  labelColor,
  body,
}: {
  label: string;
  labelColor: string;
  body: string;
}) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 500,
          color: labelColor,
          letterSpacing: "0.06em",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.875rem",
          color: "text.secondary",
          lineHeight: 1.6,
        }}
      >
        {body}
      </Typography>
    </Box>
  );
}

function StatCard({
  label,
  value,
  small,
}: {
  label: string;
  value: string;
  small?: boolean;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: "14px 16px",
        borderRadius: 1,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        border: (theme) =>
          `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.625rem",
          fontWeight: 700,
          color: "primary.main",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: small ? "0.8125rem" : "0.9375rem",
          fontWeight: 500,
          color: "text.primary",
          letterSpacing: "-0.01em",
          lineHeight: 1.35,
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
}

function CaseStudyCard({
  from,
  to,
  time,
  quote,
  id,
}: {
  from: string;
  to: string;
  time: string;
  quote: string;
  id: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 3.5 },
        borderRadius: 1.5,
        bgcolor: "background.default",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: (theme) => theme.shadows[1],
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        flexWrap="wrap"
        sx={{
          mb: 2.5,
          fontSize: "0.8125rem",
          color: "text.secondary",
          rowGap: 0.5,
        }}
      >
        <span>{from}</span>
        <ArrowForwardIcon sx={{ fontSize: 14, color: "primary.main" }} />
        <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
          {to}
        </Box>
        <Box
          sx={{
            ml: "auto",
            fontFamily: MONO,
            color: "primary.main",
            fontSize: "0.75rem",
            fontWeight: 500,
          }}
        >
          {time}
        </Box>
      </Stack>
      <Typography
        sx={{
          fontSize: "1.125rem",
          lineHeight: 1.5,
          color: "text.primary",
          mb: 2.5,
          letterSpacing: "-0.012em",
          fontWeight: 500,
        }}
      >
        "{quote}"
      </Typography>
      <Typography
        sx={{
          fontSize: "0.75rem",
          color: "text.disabled",
          fontFamily: MONO,
        }}
      >
        {id}
      </Typography>
    </Paper>
  );
}
